// =============================================================================
// BUDDY USER MODERATION MODAL — per-user detail surface for the Tenant
// Users tab. Hosts the deep view that used to live on the Tenant
// Dashboard's BuddyAbuseAnalyticsCard:
//   • Strike count + 10-strike progress bar
//   • Blocked / striker / clean status pill
//   • Pending unblock-request indicator
//   • Top abuse terms aggregated from this user's strikes
//   • Recent strike feed (last ~20) — body excerpt + matched terms +
//     when, fetched on open
//   • Inline Unblock button when the user is currently blocked
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { ShieldAlert, ShieldCheck, Ban, AlertTriangle, RotateCcw, RefreshCw, Bell, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const APP_SUPABASE_URL = import.meta.env.VITE_APP_SUPABASE_URL || '';
const PROVISION_SECRET = import.meta.env.VITE_PROVISION_SECRET || '';

async function callBuddy(body: any) {
  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/admin-buddy-analytics`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET },
    body: JSON.stringify(body),
  });
  return res.json();
}

export interface BuddyUserModerationSummary {
  user_id:             string;
  email:               string | null;
  full_name:           string | null;
  strike_count:        number;
  blocked:             boolean;
  blocked_at:          string | null;
  last_strike_at:      string | null;
  last_strike_terms:   string[] | null;
  last_strike_excerpt: string | null;
  pending_request:     boolean;
  /** Total block events (closed + currently open). */
  times_blocked?:      number;
  /** Closed block events (rows with unblocked_at filled). */
  times_unblocked?:    number;
  /** First time the user was ever blocked in this tenant. */
  first_blocked_at?:   string | null;
  /** Last time the user came back from a block. */
  last_unblocked_at?:  string | null;
}

interface UserStrike {
  id:            string;
  group_id:      string | null;
  message_body:  string | null;
  matched_terms: string[];
  created_at:    string;
}

interface BlockEvent {
  id:                       string;
  blocked_at:               string;
  unblocked_at:             string | null;
  strike_count_at_block:    number;
  matched_terms_snapshot:   string[] | null;
  excerpt_snapshot:         string | null;
}

interface UserHistory {
  total_strikes_ever: number;
  block_events:       BlockEvent[];
  current_moderation: any;
}

// Tenant-configurable fallback. The real strike limit is fetched from
// admin-buddy-analytics on open and stored in component state below.
// This constant is only used as the divide-by-zero guard during the
// brief window between modal open and the limit being fetched.
const STRIKE_LIMIT_FALLBACK = 10;

export function BuddyUserModerationModal({
  open,
  onClose,
  tenantId,
  user,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  user: BuddyUserModerationSummary | null;
  /** Called after a successful unblock so the parent can refresh
   *  the users list / moderation indexes. */
  onChanged?: () => void;
}) {
  const [strikes, setStrikes]   = useState<UserStrike[]>([]);
  const [history, setHistory]   = useState<UserHistory | null>(null);
  const [loading, setLoading]   = useState(true);
  const [unblocking, setUnblocking] = useState(false);
  // Tenant-configured strike limit, fetched on open via
  // get_resolved_feature_limit('buddy_trips_strike_limit'). Falls back
  // to the constant during the brief window before the response lands.
  const [strikeLimit, setStrikeLimit] = useState<number>(STRIKE_LIMIT_FALLBACK);

  // Current admin — captured on the unblock call so the audit trail
  // records who took the action (email + role).
  const { user: actingUser, isSuperAdmin } = useAuth();

  // Fetch the strike feed + history every time the modal opens for a
  // different user. Two parallel calls so the per-user view paints in
  // one frame.
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    setLoading(true);
    setStrikes([]);
    setHistory(null);
    setStrikeLimit(STRIKE_LIMIT_FALLBACK);
    (async () => {
      try {
        const [strikesRes, historyRes, limitRes] = await Promise.all([
          callBuddy({ mode: 'list_user_strikes',  tenant_id: tenantId, user_id: user.user_id }),
          callBuddy({ mode: 'get_user_history',   tenant_id: tenantId, user_id: user.user_id }),
          callBuddy({ mode: 'get_strike_limit',   tenant_id: tenantId, user_id: user.user_id }),
        ]);
        if (cancelled) return;
        if (strikesRes.ok) setStrikes(strikesRes.data ?? []);
        if (historyRes.ok) setHistory(historyRes.data ?? null);
        if (limitRes.ok && typeof limitRes.data?.strike_limit === 'number') {
          setStrikeLimit(limitRes.data.strike_limit);
        }
      } catch (err) {
        console.warn('[BuddyUserModerationModal] failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, tenantId, user?.user_id]);

  // Aggregate top abuse terms from this user's strike feed (last 20).
  const topTerms = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of strikes) {
      for (const t of s.matched_terms || []) counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [strikes]);

  if (!open || !user) return null;

  const name = user.full_name || user.email?.split('@')[0] || 'Unknown';
  const status: 'blocked' | 'striking' | 'clean' =
    user.blocked ? 'blocked' :
    user.strike_count > 0 ? 'striking' :
    'clean';
  const progressPct = Math.min(100, Math.round((user.strike_count / strikeLimit) * 100));

  const handleUnblock = async () => {
    if (!confirm(`Unblock ${name}? Their strike count resets to 0.`)) return;
    setUnblocking(true);
    try {
      const res = await callBuddy({
        mode: 'unblock',
        tenant_id: tenantId,
        user_id: user.user_id,
        // Audit capture — who is performing the unblock?
        acted_by_user_id: actingUser?.id ?? null,
        acted_by_email:   actingUser?.email ?? null,
        acted_by_role:    isSuperAdmin ? 'super_admin' : 'tenant_admin',
      });
      if (!res.ok) throw new Error(res.error || 'Unblock failed');
      onChanged?.();
      onClose();
    } catch (err: any) {
      alert(err?.message || 'Could not unblock.');
    } finally {
      setUnblocking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6 bg-black/50 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xl rounded-2xl bg-surface-0 border border-surface-200 shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-surface-200 flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shrink-0 ${
              status === 'blocked' ? 'bg-red-500' : status === 'striking' ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
          >
            {(name.charAt(0) || '?').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="text-base font-semibold text-surface-900 truncate">{name}</h3>
              <StatusPill status={status} />
              {user.pending_request && (
                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                  <Bell size={9} />
                  Requested unblock
                </span>
              )}
            </div>
            <p className="text-xs text-surface-500 truncate">
              {user.email || user.user_id}
              {user.blocked_at && <> · blocked {new Date(user.blocked_at).toLocaleString()}</>}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1.5 -mt-1 rounded-lg hover:bg-surface-100 text-surface-400 shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Strike progress + lifetime stats */}
        <div className="px-5 py-4 border-b border-surface-200">
          <div className="flex items-center justify-between text-xs text-surface-500 mb-1.5">
            <span>Current strike count</span>
            <span className="font-bold tabular-nums text-surface-900">
              {user.strike_count} / {strikeLimit}
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-surface-100 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                user.strike_count >= strikeLimit ? 'bg-red-500'
                  : user.strike_count >= Math.ceil(strikeLimit * 0.7) ? 'bg-amber-500'
                  : user.strike_count >= Math.ceil(strikeLimit * 0.3) ? 'bg-amber-400'
                  : 'bg-emerald-500'
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-[11px] text-surface-500 mt-1.5">
            {user.strike_count >= strikeLimit
              ? `Auto-blocked at ${strikeLimit} strikes. Unblock to reset.`
              : user.strike_count > 0
                ? `${strikeLimit - user.strike_count} more strike${strikeLimit - user.strike_count === 1 ? '' : 's'} until auto-block.`
                : 'Currently clean. Strike counter resets on each unblock.'}
          </p>

          {/* Lifetime stats — visible whenever there's a history to show
              (lifetime strikes > current count, or the user has been
              blocked at least once before). Splits block / unblock so
              admins see both counters at a glance. */}
          {history && (history.total_strikes_ever > user.strike_count || history.block_events.length > 0) && (
            <div className="mt-3 pt-3 border-t border-surface-200 flex flex-wrap items-center gap-3 text-[11px] text-surface-500">
              <span className="inline-flex items-center gap-1">
                <ShieldAlert size={11} className="text-amber-600" />
                <strong className="text-surface-900 tabular-nums">{history.total_strikes_ever}</strong> lifetime strikes
              </span>
              {history.block_events.length > 0 && (
                <>
                  <span className="inline-flex items-center gap-1">
                    <Ban size={11} className="text-red-600" />
                    <strong className="text-surface-900 tabular-nums">{history.block_events.length}</strong> block event{history.block_events.length === 1 ? '' : 's'}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                    <strong className="text-surface-900 tabular-nums">
                      {history.block_events.filter(e => e.unblocked_at).length}
                    </strong>{' '}
                    unblocked
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Block / Unblock timeline — visible when at least one event
            exists. Newest first; each row pairs blocked_at with
            unblocked_at (or "currently blocked" when open). Each
            stamp renders date + time on a single line; closed rows
            also show the block duration. */}
        {history && history.block_events.length > 0 && (
          <div className="px-5 py-4 border-b border-surface-200">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-surface-500 mb-2">
              Block / unblock history
            </p>
            <ul className="space-y-2">
              {history.block_events.map(ev => {
                const open = !ev.unblocked_at;
                return (
                  <li
                    key={ev.id}
                    className={`text-[11px] px-3 py-2 rounded-lg border ${
                      open
                        ? 'bg-red-50/60 dark:bg-red-950/20 border-red-200/60 dark:border-red-800/40'
                        : 'bg-surface-50/70 border-surface-200/60'
                    }`}
                  >
                    {/* Two-row stamp pair so date/time read clean */}
                    <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 items-center">
                      <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400 font-bold uppercase tracking-wider text-[10px]">
                        <Ban size={10} /> Blocked
                      </span>
                      <span className="font-semibold text-surface-900 tabular-nums">
                        {formatDateTime(ev.blocked_at)}
                      </span>

                      {open ? (
                        <>
                          <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400 font-bold uppercase tracking-wider text-[10px]">
                            Status
                          </span>
                          <span className="font-bold text-red-700 dark:text-red-400 uppercase tracking-wider text-[10px]">
                            Currently blocked
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-bold uppercase tracking-wider text-[10px]">
                            <ShieldCheck size={10} /> Unblocked
                          </span>
                          <span className="font-semibold text-surface-900 tabular-nums">
                            {formatDateTime(ev.unblocked_at!)}
                          </span>

                          <span className="inline-flex items-center gap-1 text-surface-500 uppercase tracking-wider text-[10px]">
                            Duration
                          </span>
                          <span className="text-surface-700 tabular-nums">
                            {formatDuration(ev.blocked_at, ev.unblocked_at!)}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Trigger context: what tipped them over to 10 strikes */}
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap text-surface-500">
                      <span className="text-amber-700 dark:text-amber-400 tabular-nums font-semibold">
                        {ev.strike_count_at_block} strikes at block
                      </span>
                      {ev.matched_terms_snapshot && ev.matched_terms_snapshot.length > 0 && (
                        <span className="text-amber-700 dark:text-amber-400 font-mono text-[10px]">
                          · {ev.matched_terms_snapshot.slice(0, 5).join(', ')}
                        </span>
                      )}
                    </div>
                    {ev.excerpt_snapshot && (
                      <p className="text-surface-500 italic mt-1 break-words">
                        "{ev.excerpt_snapshot}"
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Top terms */}
        {topTerms.length > 0 && (
          <div className="px-5 py-3 border-b border-surface-200">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-surface-500 mb-2">
              Top abuse terms
            </p>
            <div className="flex flex-wrap gap-1.5">
              {topTerms.map(([term, count]) => (
                <span
                  key={term}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 text-amber-800 dark:text-amber-300"
                >
                  <span className="font-mono">{term}</span>
                  <span className="opacity-70">· {count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Recent strikes */}
        <div className="px-5 py-4 flex-1 overflow-y-auto">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-surface-500 mb-2">
            Recent strikes
          </p>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw size={18} className="animate-spin text-surface-400" />
            </div>
          ) : strikes.length === 0 ? (
            <p className="text-xs text-surface-500 italic py-4 text-center">
              No strike audit rows recorded.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {strikes.map(s => (
                <li
                  key={s.id}
                  className="text-[11px] flex items-start gap-2 px-2.5 py-1.5 rounded-lg bg-surface-50/70 dark:bg-surface-100/30 border border-surface-200/60"
                >
                  <ShieldAlert size={11} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap text-surface-500">
                      <span className="font-semibold text-surface-700">
                        {new Date(s.created_at).toLocaleString()}
                      </span>
                      {s.matched_terms && s.matched_terms.length > 0 && (
                        <>
                          <span className="text-surface-400">·</span>
                          <span className="text-amber-700 dark:text-amber-400 font-mono text-[10px]">
                            {s.matched_terms.slice(0, 4).join(', ')}
                          </span>
                        </>
                      )}
                    </div>
                    {s.message_body && (
                      <p className="text-surface-600 italic mt-0.5 break-words">
                        "{s.message_body}"
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-surface-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-surface-700 hover:bg-surface-100 transition-colors"
          >
            Close
          </button>
          {user.blocked && (
            <button
              onClick={handleUnblock}
              disabled={unblocking}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {unblocking
                ? <RefreshCw size={12} className="animate-spin" />
                : <RotateCcw size={12} />}
              Unblock user
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Human-readable date+time, locale-aware: "5/9/2026, 2:50 PM".
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// Compact duration between two ISO timestamps. "2d 6h", "45m", "1h 12m".
function formatDuration(startIso: string, endIso: string): string {
  const ms = Math.max(0, new Date(endIso).getTime() - new Date(startIso).getTime());
  const days  = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const mins  = Math.floor((ms % 3_600_000) / 60_000);
  if (days >= 1)  return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${mins}m`;
  if (mins  >= 1) return `${mins}m`;
  return '<1m';
}

function StatusPill({ status }: { status: 'blocked' | 'striking' | 'clean' }) {
  if (status === 'blocked') {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
        <Ban size={9} /> Blocked
      </span>
    );
  }
  if (status === 'striking') {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
        <AlertTriangle size={9} /> Striker
      </span>
    );
  }
  return null;
}
